import { concatBytes, fromUtf8, utf8 } from './bytes.ts';

/**
 * y.dk's on-chain record: the CBOR map (RFC 8949, deterministic encoding) in
 * the `data` of a Concordium RegisterData transaction. Ported from
 * backend-y-new `provenance/concordium/record.ts` to plain Uint8Array so it
 * runs in a browser.
 *
 *   { "c": "v2", "h": "<sha256 hex>", "t": "article",
 *     "u": "https://<site>/<uid>", "v": 1, "id": "<story uid>" }
 */
export type ProvenanceRecord = {
  id: string;
  v: number;
  t: string;
  h: string;
  c: string;
  u?: string;
};

export const MAX_REGISTER_DATA_BYTES = 256;

export function encodeRecord(record: ProvenanceRecord): Uint8Array {
  const entries: Array<[string, string | number]> = [
    ['id', record.id],
    ['v', record.v],
    ['t', record.t],
    ['h', record.h],
    ['c', record.c],
    ...(record.u ? [['u', record.u] as [string, string]] : []),
  ];
  const encoded = entries
    .map(([k, v]) => ({ key: textString(k), value: typeof v === 'number' ? head(0, v) : textString(v) }))
    .sort((a, b) => compare(a.key, b.key));
  const out = concatBytes(head(5, encoded.length), ...encoded.flatMap((e) => [e.key, e.value]));
  if (out.length > MAX_REGISTER_DATA_BYTES) throw new Error(`record is ${out.length} bytes (max ${MAX_REGISTER_DATA_BYTES})`);
  return out;
}

/** Reads a record back from transaction data; throws on anything else. */
export function decodeRecord(buf: Uint8Array): ProvenanceRecord {
  let pos = 0;
  const readHead = (): { major: number; value: number } => {
    if (pos >= buf.length) throw new Error('CBOR: truncated');
    const byte = buf[pos++];
    const major = byte >> 5;
    const info = byte & 0x1f;
    if (info < 24) return { major, value: info };
    const size = info === 24 ? 1 : info === 25 ? 2 : info === 26 ? 4 : 0;
    if (!size || pos + size > buf.length) throw new Error('CBOR: unsupported length');
    let value = 0;
    for (let i = 0; i < size; i++) value = value * 256 + buf[pos + i];
    pos += size;
    return { major, value };
  };
  const readText = (): string => {
    const { major, value } = readHead();
    if (major !== 3) throw new Error('CBOR: expected a text string');
    if (pos + value > buf.length) throw new Error('CBOR: truncated');
    const text = fromUtf8(buf.subarray(pos, pos + value));
    pos += value;
    return text;
  };
  const map = readHead();
  if (map.major !== 5) throw new Error('CBOR: expected a map');
  const out: Record<string, string | number> = {};
  for (let i = 0; i < map.value; i++) {
    const key = readText();
    out[key] = buf[pos] >> 5 === 0 ? readHead().value : readText();
  }
  if (pos !== buf.length) throw new Error('CBOR: trailing bytes');
  const { id, v, t, h, c, u } = out;
  if (
    typeof id !== 'string' || typeof v !== 'number' || typeof t !== 'string' ||
    typeof h !== 'string' || typeof c !== 'string' || (u !== undefined && typeof u !== 'string')
  ) {
    throw new Error('CBOR: not a provenance record');
  }
  return u === undefined ? { id, v, t, h, c } : { id, v, t, h, c, u };
}

function compare(a: Uint8Array, b: Uint8Array): number {
  for (let i = 0; i < Math.min(a.length, b.length); i++) if (a[i] !== b[i]) return a[i] - b[i];
  return a.length - b.length;
}

function head(major: number, value: number): Uint8Array {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error('CBOR: negative or unsafe integer');
  if (value < 24) return Uint8Array.of((major << 5) | value);
  if (value < 0x100) return Uint8Array.of((major << 5) | 24, value);
  if (value < 0x10000) return Uint8Array.of((major << 5) | 25, value >> 8, value & 0xff);
  if (value < 0x100000000) {
    return Uint8Array.of((major << 5) | 26, (value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff);
  }
  throw new Error('CBOR: integer too large');
}

function textString(text: string): Uint8Array {
  const bytes = utf8(text);
  return concatBytes(head(3, bytes.length), bytes);
}
